/**
 * DevToolsPanel - Persistent DevTools panel at bottom of production workspace
 *
 * A persistent panel that survives navigation between production screens.
 * Similar to browser DevTools - collapsible, sticky at bottom, tabbed interface.
 *
 * Features:
 * - Fixed positioning at bottom of screen
 * - Collapsible with state persisted in localStorage
 * - Two tabs: "Test QRs" (existing DevQRTestPanel) and "Garment Tracker" (Plan 02)
 * - z-30: below modals (z-50) AND below page chrome such as the z-40
 *   PageActionBar (the manual order page renders one under ProductionLayout).
 *   An admin aid must never cover product chrome.
 * - max-h-[35vh] when expanded to preserve content space
 * - Yellow accent styling matching DevQRTestPanel
 *
 * Visibility controlled by useProductionSettings.showDevTools
 */
import { useState, useEffect, useRef } from 'react';
import { Beaker, ChevronDown, ChevronUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import { DevQRTestPanel } from '@/features/catcher-station/components/DevQRTestPanel';
import { LiveStatusTable } from './LiveStatusTable';
import { SeedBatchControls } from './SeedBatchControls';

const DEVTOOLS_EXPANDED_KEY = 'devtools-expanded';

export function DevToolsPanel() {
  const { settings } = useProductionSettings();

  // Persist collapse state in localStorage
  const [isExpanded, setIsExpanded] = useState(() => {
    return localStorage.getItem(DEVTOOLS_EXPANDED_KEY) === 'true';
  });
  const [activeTab, setActiveTab] = useState<'qr' | 'status'>('qr');

  useEffect(() => {
    localStorage.setItem(DEVTOOLS_EXPANDED_KEY, String(isExpanded));
  }, [isExpanded]);

  // Publish the fixed panel's rendered height as `--devtools-bar-height` so a
  // full-viewport surface (the Client messages inbox) is not covered by it.
  // Resets to 0px when hidden/unmounted. Same convention as PageActionBar.
  const panelRef = useRef<HTMLDivElement>(null);
  const visible = settings.showDevTools;
  useEffect(() => {
    const el = panelRef.current;
    if (!visible || !el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty('--devtools-bar-height', `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--devtools-bar-height', '0px');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    // z-30: below modals (z-50) and below the z-40 PageActionBar (DESIGN.md §2)
    <div ref={panelRef} className="fixed bottom-0 left-0 right-0 z-30 border-t bg-background shadow-lg">
      {/* Collapse bar - always visible with yellow accent */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full h-10 flex items-center justify-center gap-2 bg-yellow-50 dark:bg-yellow-950/20 hover:bg-yellow-100 dark:hover:bg-yellow-950/30 transition-colors"
        aria-expanded={isExpanded}
        aria-controls="devtools-panel-content"
      >
        <Beaker className="h-4 w-4 text-yellow-600" aria-hidden="true" />
        <span className="text-sm font-medium text-yellow-700 dark:text-yellow-500">
          DevTools
        </span>
        <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-700 dark:text-yellow-500">
          DEV
        </Badge>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground ml-2" />
        ) : (
          <ChevronUp className="h-4 w-4 text-muted-foreground ml-2" />
        )}
      </button>

      {/* Panel content when expanded - max 35vh to preserve content space */}
      {isExpanded && (
        <div id="devtools-panel-content" className="max-h-[35vh] overflow-auto border-t">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'qr' | 'status')}>
            <TabsList className="grid w-full grid-cols-2 h-auto gap-1 p-1 bg-muted/50">
              <TabsTrigger value="qr" className="text-sm py-2.5 gap-1.5">
                Test QRs
              </TabsTrigger>
              <TabsTrigger value="status" className="text-sm py-2.5 gap-1.5">
                Garment Tracker
              </TabsTrigger>
            </TabsList>
            <TabsContent value="qr" className="mt-0">
              <DevQRTestPanel mode="embedded" />
            </TabsContent>
            <TabsContent value="status" className="mt-0">
              <SeedBatchControls />
              <LiveStatusTable />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
